import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import {
  useCreatePvxPartnerMutation,
  useUpdatePvxPartnerDashboardsMutation,
  useUpdatePvxPartnerFilterEntitlementsMutation,
} from '@api/pvxPartners';
import { FilterEntitlementEntry, PVX_PARTNERS_TYPE } from '@api/types/pvxPartners';
import { PvxPartnerDetailsFormData } from '../types/partnerDetails';
import { DashboardSelectorSubmoduleItem } from '../types/dashboardSelector';

interface CreatePvxPartnerParams {
  partnerDetails: PvxPartnerDetailsFormData;
  submodules: DashboardSelectorSubmoduleItem[];
  appAccess: FilterEntitlementEntry;
  companyAccess: FilterEntitlementEntry;
}

type UseCreatePvxPartnerParams = {
  onSuccess: () => void;
};

type UseCreatePvxPartnerResult = {
  isSaving: boolean;
  handleCreate: (params: CreatePvxPartnerParams) => Promise<void>;
};

export const useCreatePvxPartner = ({
  onSuccess,
}: UseCreatePvxPartnerParams): UseCreatePvxPartnerResult => {
  const dispatch = useDispatch();
  const [createPartner, { isLoading: isCreating }] = useCreatePvxPartnerMutation();
  const [updateDashboards, { isLoading: isUpdatingDashboards }] =
    useUpdatePvxPartnerDashboardsMutation();
  const [updateFilterEntitlements, { isLoading: isUpdatingEntitlements }] =
    useUpdatePvxPartnerFilterEntitlementsMutation();

  const handleCreate = async ({
    partnerDetails,
    submodules,
    appAccess,
    companyAccess,
  }: CreatePvxPartnerParams) => {
    // Step 1: Create the partner record.
    let clientCode: string;
    try {
      const { data: createdPartner } = await createPartner({
        name: partnerDetails.name,
        address: partnerDetails.address || undefined,
        partnerType: partnerDetails.partnerType as PVX_PARTNERS_TYPE,
        entityType: partnerDetails.entityType,
        pvxPocEmail: partnerDetails.pvxPocEmail || undefined,
        email: partnerDetails.email,
        username: partnerDetails.username,
      }).unwrap();
      clientCode = createdPartner.clientCode;
    } catch {
      dispatch(setToastMessage({ type: TOAST_TYPES.ERROR, message: 'Failed to create partner' }));
      return;
    }

    // Step 2: Assign dashboards. Partner already exists — navigate on failure so
    // the user can fix it from the edit screen rather than losing the record.
    const submoduleIds = submodules.map((s) => s.submoduleId);
    if (submoduleIds.length > 0) {
      try {
        await updateDashboards({ clientCode, submoduleIds }).unwrap();
      } catch {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message:
              'Partner created, but dashboard setup failed. Please retry from the edit screen.',
          }),
        );
        onSuccess();
        return;
      }
    }

    // Step 3: Assign filter entitlements.
    try {
      await updateFilterEntitlements({ clientCode, filters: appAccess }).unwrap();
      await updateFilterEntitlements({ clientCode, filters: companyAccess }).unwrap();
    } catch {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Partner created, but access setup failed. Please retry from the edit screen.',
        }),
      );
      onSuccess();
      return;
    }

    dispatch(
      setToastMessage({ type: TOAST_TYPES.SUCCESS, message: 'Partner created successfully' }),
    );
    onSuccess();
  };

  return {
    isSaving: isCreating || isUpdatingDashboards || isUpdatingEntitlements,
    handleCreate,
  };
};
